import type { OptionalModule } from '@/lib/modules'

/** Canonical staff roles stored in users.role (JWT + localStorage). */
export const STAFF_ROLES = [
  'admin',
  'manager',
  'inventory_manager',
  'counter',
  'accountant',
] as const
export type StaffRole = (typeof STAFF_ROLES)[number]

export function isStaffRole(role: string): role is StaffRole {
  return (STAFF_ROLES as readonly string[]).includes(role)
}

const ROLE_ADMIN_PATHS: Record<StaffRole, string[] | null> = {
  admin: null,
  manager: [
    '/admin/counter',
    '/admin/online-orders',
    '/admin/delivery-zones',
    // Bank card promos rotate every few months and the floor manager holds
    // the bank's campaign letter — deliberate manager surface (backend
    // group: adminManagerWrite). See audit/qa/01_auth_roles.csv.
    //
    // This is the ONLY /admin/marketing/* path a manager may open: the rest
    // of the Marketing Suite (promo codes, loyalty, campaigns, segments) is
    // admin-only. Listing the leaf path — not the '/admin/marketing' parent —
    // is what keeps that true, since canAccessAdminRoute matches by prefix.
    '/admin/marketing/card-promos',
    // Legacy top-level URL, kept so bookmarks survive; the route redirects to
    // the Marketing Suite tab above.
    '/admin/card-promos',
    '/admin/catalog',
    // Legacy URL of the catalog page; the route redirects to /admin/catalog.
    '/admin/menu',
    '/admin/staff',
    '/admin/void-log',
    '/admin/settings',
    '/admin/day-close',
    '/admin/reports',
    '/admin/expenses',
    '/admin/customers',
  ],
  // The inventory manager owns stock, and in retail that means owning the
  // product catalogue too — every product is a stock unit (F-RETAIL-06).
  inventory_manager: ['/admin/inventory', '/admin/catalog', '/admin/menu'],
  counter: ['/admin/counter', '/admin/online-orders'],
  accountant: ['/admin/reports'],
}

/**
 * First screen after login for each role. Pass `isHidden` (from
 * useModuleVisibility) so a role whose usual landing page belongs to a module
 * this deployment hides lands somewhere it can still open — otherwise the
 * admin route guard would bounce it straight back into the hidden page.
 */
export function defaultAdminPath(
  role: string,
  isHidden: (m: OptionalModule) => boolean = () => false,
): string {
  if (role === 'inventory_manager' && isHidden('inventory')) return '/admin/catalog'
  switch (role) {
    case 'admin':
      return '/admin/dashboard'
    case 'manager':
      return '/admin/counter'
    case 'inventory_manager':
      return '/admin/inventory'
    case 'counter':
      return '/admin/counter'
    case 'accountant':
      return '/admin/reports'
    default:
      return '/admin/dashboard'
  }
}

/** Whether this role may open the given /admin/* URL (exact prefix match). */
export function canAccessAdminRoute(role: string, pathname: string): boolean {
  if (!pathname.startsWith('/admin')) return false
  if (role === 'admin') return true
  const prefixes = ROLE_ADMIN_PATHS[role as StaffRole]
  if (!prefixes) return false
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** Sidebar section ids visible for the role; null means all (admin). */
export function navSectionIdsForRole(role: string): Set<string> | null {
  if (role === 'admin') return null
  if (role === 'inventory_manager') return new Set(['inventory', 'catalog'])
  if (role === 'manager') {
    return new Set([
      'counter',
      'online-orders',
      'delivery-zones',
      // Bank card promos moved inside the Marketing Suite; managers get the
      // section for that one tab. marketingLandingPath() sends them straight
      // to it, and MarketingShell hides the tabs they can't open.
      'marketing',
      'catalog',
      'staff',
      'void-log',
      'settings',
      'day-close',
      'reports',
      'expenses',
      'customers',
    ])
  }
  if (role === 'counter') return new Set(['counter', 'online-orders'])
  if (role === 'accountant') return new Set(['reports'])
  return new Set()
}

/**
 * Where the "Marketing Suite" nav item points for this role. Managers may
 * only open the Bank card promos tab, so sending them to /overview — which
 * the route guard bounces — would make the nav item a dead link.
 */
export function marketingLandingPath(role: string): string {
  return role === 'manager' ? '/admin/marketing/card-promos' : '/admin/marketing/overview'
}

export function staffRoleLabel(role: string): string {
  switch (role) {
    case 'inventory_manager':
      return 'Inventory'
    case 'counter':
      return 'Counter'
    case 'admin':
      return 'Admin'
    case 'manager':
      return 'Manager'
    case 'accountant':
      return 'Accountant'
    default:
      return role
  }
}

/**
 * The current user's role from localStorage (`pos_user`), or '' if unknown.
 * UI convenience only — real authorization is the backend route guard.
 */
export function getStoredUserRole(): string {
  try {
    const raw = localStorage.getItem('pos_user')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { role?: string }
    return parsed.role ?? ''
  } catch {
    return ''
  }
}
